import React from 'react';
import {
  FlowFilter,
  flowComboboxItem,
} from '@hud/web-components/Filters/FlowFilter';
import styled from 'styled-components';
import { ComboboxItemGroup } from '@mantine/core/lib/components/Combobox/Combobox.types';
import { FlowType } from '@hud/web-components/utils';

const Container = styled.div``;

// For backwards compatibility: remove this type after all clients update from v1.1.32 in vscode & v1.0.1 in JB (28.11.2024)
type OldFlow = {
  id: number;
  runs: number;
  path: string;
  methods: string[];
  services: {
    id: number;
    name: string;
  }[];
};

type BasicFlow = {
  id: number;
  runs: number;
  service: {
    id: number;
    name: string;
  };
};

export type Endpoint = BasicFlow & {
  type: 'ENDPOINT';
  path: string;
  methods: string[];
};

export type KafkaTopic = BasicFlow & {
  type: 'KAFKA';
  kafka_topic_name: string;
  kafka_topic_group_id: string;
};

export type SqsQueue = BasicFlow & {
  type: 'SQS';
  sqs_queue_name: string;
};

export type Flow = Endpoint | KafkaTopic | SqsQueue;
//guards
export function isEndpoint(flow: Flow): flow is Endpoint {
  return flow.type === 'ENDPOINT';
}
export function isKafkaTopic(flow: Flow): flow is KafkaTopic {
  return flow.type === 'KAFKA';
}
export function isSqsQueue(flow: Flow): flow is SqsQueue {
  return flow.type === 'SQS';
}
export function isFlow(flow: Flow | OldFlow): flow is Flow {
  return 'service' in flow;
}

function getFlowDisplayName(flow: Flow) {
  if (isEndpoint(flow)) {
    return flow.path;
  }
  if (isKafkaTopic(flow)) {
    //TODO: add check if multi_group and add group id to the name if needed
    return flow.kafka_topic_name;
  }
  if (isSqsQueue(flow)) {
    return flow.sqs_queue_name;
  }
  return '';
}

type ServiceToFlows = {
  [key: string]: {
    flowId: number;
    serviceId: number;
    path: string;
    flowType: FlowType;
  }[];
};

export type ServiceAndFlow = {
  service: string;
  flow: string;
};

export function hashServiceAndFlow(
  serviceId: string | null,
  flowId: string | null,
) {
  return serviceId && flowId && `${serviceId}|${flowId}`;
}

export function unHashServiceAndFlow(serviceFlowHash: string): ServiceAndFlow {
  return {
    service: serviceFlowHash.split('|')[0],
    flow: serviceFlowHash.split('|')[1],
  };
}

function formatFlowList(
  flowList: Flow[] | OldFlow[],
): ComboboxItemGroup<flowComboboxItem>[] {
  const serviceToFlows: ServiceToFlows = {};
  // map service to flow
  if (flowList.length === 0) {
    return [];
  }
  if (isFlow(flowList[0])) {
    flowList = flowList as Flow[];
    flowList.forEach((item) => {
      serviceToFlows[item.service.name] =
        serviceToFlows[item.service.name] || [];
      serviceToFlows[item.service.name].push({
        flowId: item.id,
        serviceId: item.service.id,
        path: getFlowDisplayName(item),
        flowType: item.type,
      });
    });
  } else {
    // backwards compatibility, remove after all clients update from v1.1.32 in vscode & v1.0.1 in JB (28.11.2024)
    flowList = flowList as OldFlow[];
    flowList.forEach((item) => {
      item.services.forEach((service) => {
        serviceToFlows[service.name] = serviceToFlows[service.name] || [];
        serviceToFlows[service.name].push({
          flowId: item.id,
          serviceId: service.id,
          path: item.path,
          flowType: 'ENDPOINT',
        });
      });
    });
  }

  // generate the data structure for the component
  // note - the format of `serviceId|flowId` is a lesser-of-two-evils solution
  // to the fact that we filter by both service and flow, but the flow id isn't tied to a specific service
  return Object.keys(serviceToFlows).map((serviceName) => ({
    group: serviceName,
    items: serviceToFlows[serviceName].map((serviceData) => ({
      value: `${serviceData.serviceId}|${serviceData.flowId}`,
      label: serviceData.path,
      flowType: serviceData.flowType,
    })),
  }));
}

interface FlowFilterRendererProps {
  flows: Flow[] | null | undefined;
  selectedFlow: string | null; // this represents both the service and the flow
  onSelectFlow: (value: string) => void;
  onClear: () => void;
  loading?: boolean;
}

export default function FlowFilterRenderer(props: FlowFilterRendererProps) {
  const serviceFlowFilterList = formatFlowList(props.flows ?? []);

  return (
    <Container>
      <FlowFilter
        data={serviceFlowFilterList}
        selectedFlow={props.selectedFlow ?? ''}
        onClear={props.onClear}
        onSelectFlow={props.onSelectFlow}
        loading={props.loading}
      />
    </Container>
  );
}
